import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { SynapseAnalyticsSettings } from 'app/model/dataflow/dataset-settings/synapse-analytics-settings';
import { IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { BaseSqlFormatAdapter } from 'app/model/adapters/dataflow/formatAdapters/sql-base.adapter';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DatasetUtils } from 'app/model/dataset/dataset.utils';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowAdapterUtils } from '../adapter.util';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(SynapseAnalyticsSettings)
})
export class SynapseAnalyticsFormatAdapter extends BaseSqlFormatAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined, format?: string): void {
        if (linkedService) {
            const dbName = DatasetUtils.getDatabaseFromLinkedService(linkedService);
            if (dbName) {
                props.push(`database: ${DataFlowAdapterUtils.formatExpressionValue(dbName)}`);
            }
        }

        if (isInline || !dataset) {
            return;
        }

        props.push(`store: '${DatasetFormatType.SynapseAnalytics}'`);

        DataFlowAdapterUtils.supplementSqlSchemaAndTableFromDataset(dataset, props, isSource, format);
    }

    protected async toSourceDSL(synapseAnalyticsSettings: SynapseAnalyticsSettings): Promise<string[]> {
        const props = await super.toSourceDSL(synapseAnalyticsSettings);
        return props.concat(this._commonToSynapseAnalyticsDSL(synapseAnalyticsSettings));
    }

    protected async toSinkDSL(synapseAnalyticsSettings: SynapseAnalyticsSettings): Promise<string[]> {
        const props = await super.toSinkDSL(synapseAnalyticsSettings);
        if (synapseAnalyticsSettings.staged && synapseAnalyticsSettings.useCopyCommandForStaging) {
            props.push(`allowCopyCommand: true`);
        }
        return props.concat(this._commonToSynapseAnalyticsDSL(synapseAnalyticsSettings));
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[],
        settings?: SynapseAnalyticsSettings
    ): Promise<SynapseAnalyticsSettings> {
        if (settings) {
            await super.toSourceModel(owner, configs, handledConfigs, settings);
            return this._commonToSynapseAnalyticsModel(owner, configs, handledConfigs, settings);
        }
        const synapseAnalyticsSettings = Owned.create<SynapseAnalyticsSettings>(SynapseAnalyticsSettings.MODEL, owner);
        synapseAnalyticsSettings.initialize(undefined);
        await super.toSourceModel(owner, configs, handledConfigs, synapseAnalyticsSettings);
        return this._commonToSynapseAnalyticsModel(owner, configs, handledConfigs, synapseAnalyticsSettings);
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        settings?: SynapseAnalyticsSettings
    ): Promise<SynapseAnalyticsSettings> {
        if (settings) {
            settings.useCopyCommandForStaging =
                configs.has('allowCopyCommand') && configs.get('allowCopyCommand') === 'true';
            handledConfigs.push('allowCopyCommand');
            await super.toSinkModel(owner, configs, handledConfigs, settings);
            return this._commonToSynapseAnalyticsModel(owner, configs, handledConfigs, settings);
        }
        const synapseAnalyticsSettings = Owned.create<SynapseAnalyticsSettings>(SynapseAnalyticsSettings.MODEL, owner);
        synapseAnalyticsSettings.initialize(undefined);
        synapseAnalyticsSettings.useCopyCommandForStaging =
            configs.has('allowCopyCommand') && configs.get('allowCopyCommand') === 'true';
        handledConfigs.push('allowCopyCommand');
        await super.toSinkModel(owner, configs, handledConfigs, synapseAnalyticsSettings);
        return this._commonToSynapseAnalyticsModel(owner, configs, handledConfigs, synapseAnalyticsSettings);
    }

    private _commonToSynapseAnalyticsDSL(synapseAnalyticsSettings: SynapseAnalyticsSettings): string[] {
        const props = [];
        props.push(`staged: ${synapseAnalyticsSettings.staged}`);
        if (synapseAnalyticsSettings.batchSize.expression && synapseAnalyticsSettings.staged) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    synapseAnalyticsSettings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        return props;
    }

    private _commonToSynapseAnalyticsModel(
        owner: DataFlowSource | DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        synapseAnalyticsSettings: SynapseAnalyticsSettings
    ): SynapseAnalyticsSettings {
        if (configs.has('staged')) {
            synapseAnalyticsSettings.staged = configs.get('staged') === 'true';
            handledConfigs.push('staged');
        }
        if (configs.has('batchSize')) {
            synapseAnalyticsSettings.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        return synapseAnalyticsSettings;
    }
}
